import {rankWith} from "@jsonforms/core";
import {materialRenderers} from "@jsonforms/material-renderers";
import {t} from "i18next";
import {AutocompleteRenderer, AutocompleteTester} from "./components/AutocompleteRenderer";
import {ColorPickerRenderer, ColorPickerTester} from "./components/ColorPickerRenderer";
import {DatePickerRenderer, DatePickerTester} from "./components/DatePickerRenderer";
import {EmailFieldRenderer, EmailFieldTester} from "./components/EmailRenderer";
import {FileHandlerRenderer, FileHandlerTester} from "./components/FileHandlerRenderer";
import {FormListRenderer, FormListTester} from "./components/FormListRenderer";
import {GroupedSelectRenderer, GroupedSelectTester} from "./components/GroupedSelectRenderer";
import {HierarchicalSelectRenderer, HierarchicalSelectTester} from "./components/HierarchicalSelectRenderer";
import {HtmlEditorRenderer, HtmlEditorTester} from "./components/HtmlEditorRenderer";
import {I18nAutocompleteRenderer, I18nAutocompleteTester} from "./components/I18nAutocompleteRenderer";
import {I18nHtmlEditorRenderer, I18nHtmlEditorTester} from "./components/I18nHtmlEditorRenderer";
import {I18nTextAreaRenderer, I18nTextAreaTester} from "./components/I18nTextAreaRenderer";
import {I18nTextFieldRenderer, I18nTextFieldTester} from "./components/I18nTextFieldRenderer";
import {ImagePickerRenderer, ImagePickerTester} from "./components/ImagePickerRenderer";
import {SelectRenderer, SelectTester} from "./components/SelectRenderer";
import {WebsiteFieldRenderer, WebsiteFieldTester} from "./components/WebsiteRenderer";

// List of custom renderers used in JsonForms
export const getRenderers = showValidation => [
  {
    tester: rankWith(20, FormListTester),
    renderer: props => <FormListRenderer {...props} showValidation={showValidation} />
  },
  {
    tester: rankWith(20, I18nAutocompleteTester),
    renderer: props => <I18nAutocompleteRenderer {...props} />
  },
  {
    tester: rankWith(20, I18nTextAreaTester),
    renderer: props => <I18nTextAreaRenderer {...props} />
  },
  {
    tester: rankWith(20, I18nHtmlEditorTester),
    renderer: props => <I18nHtmlEditorRenderer {...props} />
  },
  {
    tester: rankWith(20, I18nTextFieldTester),
    renderer: props => <I18nTextFieldRenderer {...props} />
  },
  {
    tester: rankWith(20, HtmlEditorTester),
    renderer: props => <HtmlEditorRenderer {...props} />
  },
  {
    tester: rankWith(20, HierarchicalSelectTester),
    renderer: props => <HierarchicalSelectRenderer {...props} />
  },
  {
    tester: rankWith(20, ColorPickerTester),
    renderer: props => <ColorPickerRenderer {...props} />
  },
  {
    tester: rankWith(20, AutocompleteTester),
    renderer: props => <AutocompleteRenderer {...props} />
  },
  {
    tester: rankWith(20, GroupedSelectTester),
    renderer: props => <GroupedSelectRenderer {...props} />
  },
  {
    tester: rankWith(20, SelectTester),
    renderer: props => <SelectRenderer {...props} />
  },
  {
    tester: rankWith(20, DatePickerTester),
    renderer: props => <DatePickerRenderer {...props} />
  },
  {
    tester: rankWith(20, FileHandlerTester),
    renderer: props => <FileHandlerRenderer {...props} />
  },
  {
    tester: rankWith(20, EmailFieldTester),
    renderer: props => <EmailFieldRenderer {...props} />
  },
  {
    tester: rankWith(20, WebsiteFieldTester),
    renderer: props => <WebsiteFieldRenderer {...props} />
  },
  {
    tester: rankWith(20, ImagePickerTester),
    renderer: props => <ImagePickerRenderer {...props} />
  },
  ...materialRenderers
];

// JsonForms native elements customizations
export const jsonformsThemeCustomization = {
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          color: "#000000",
          boxShadow: "none",
          fontWeight: 500,
          fontSize: "0.875rem"
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          marginBottom: "8px"
        },
        indicator: {
          backgroundColor: ({theme}) => theme.palette.primary.main
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: ({theme}) => theme.palette.primary.main
          }
        }
      }
    },
    MuiFormControl: {
      defaultProps: {
        variant: "outlined"
      },
      styleOverrides: {
        root: {
          margin: "8px 0"
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined"
      }
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined"
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6"
        }
      },
      styleOverrides: {
        root: {
          paddingBottom: 0
        },
        title: {
          fontWeight: 400,
          fontSize: "1rem"
        }
      }
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined"
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 0,
          "&.Mui-focused:not(.Mui-error)": {
            display: "none"
          }
        }
      }
    }
  }
};

// Helper functions
export const isFieldMultilang = field => Boolean(field?.patternProperties?.[".*"]);
export const isFieldMultirows = field => Boolean(field?.options?.rows && field.options.rows > 1);
export const isFieldSelect = field =>
  Boolean(
    (Array.isArray(field.type) ? field.type.includes("string") : field.type === "string") &&
      Array.isArray(field?.elements)
  );

// Translations
t(`components.customJsonForms.error.required`);
t(`components.customJsonForms.error.minLength`);
t(`components.customJsonForms.error.maxLength`);
t(`components.customJsonForms.error.pattern`);

export const pruneEmptyValues = obj => {
  const result = Object.entries(obj)
    .filter(([, value]) => {
      if (value === undefined || value === null) {
        return false;
      }

      if (typeof value === "string") {
        return value !== "";
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === "object") {
        return Object.keys(value).length > 0;
      }

      return true;
    })
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  return Object.keys(result).length > 0 ? result : undefined;
};
